import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, FormControlLabel } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {
  HEALTH_GOAL_LABEL_KEYS,
  HEALTH_GOALS,
  isKnownHealthGoal,
} from '../../../common/utils/video-placement-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoHealthGoalsFieldProps {
  goals: string[];
  onChange: (goals: string[]) => void;
  isDisabled?: boolean;
}

const OvVideoHealthGoalsField: FC<OvVideoHealthGoalsFieldProps> = ({
  goals,
  onChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const unknownGoals = goals.filter((goal) => !isKnownHealthGoal(goal));

  const toggle = (goal: string) =>
    onChange(
      goals.includes(goal)
        ? goals.filter((selected) => selected !== goal)
        : [...goals, goal]
    );

  return (
    <Fieldset disabled={isDisabled}>
      <Legend>{t('videos.fields.healthGoals')}</Legend>
      <Options>
        {HEALTH_GOALS.map((goal) => (
          <FormControlLabel
            key={goal}
            label={t(HEALTH_GOAL_LABEL_KEYS[goal])}
            control={
              <Checkbox
                size="small"
                checked={goals.includes(goal)}
                disabled={isDisabled}
                onChange={() => toggle(goal)}
              />
            }
          />
        ))}
      </Options>
      {unknownGoals.map((goal) => (
        <Locked key={goal}>
          <LockedGoal>
            <LockIcon aria-hidden="true" />
            {goal}
          </LockedGoal>
          <Hint>{t('videos.healthGoals.unknownLocked')}</Hint>
        </Locked>
      ))}
      <Hint>{t('videos.placement.healthGoalsHint')}</Hint>
    </Fieldset>
  );
};

export default OvVideoHealthGoalsField;

const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const Legend = styled.legend`
  margin-bottom: 0.25rem;
  padding: 0;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;

  .MuiFormControlLabel-label {
    color: ${Colours.OV_BASE};
  }

  .Mui-checked + .MuiFormControlLabel-label {
    font-weight: bold;
  }
`;

const Locked = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
`;

const LockedGoal = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background-color: ${Colours.OV_DISABLED};
  font-size: ${Variables.fontSizes.SMALL};

  .MuiSvgIcon-root {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const Hint = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;
